/**
 * FollowUpAgendaKit.js
 *
 * Premium building blocks of the Teendők (sales agenda) screen: compact header with a slim progress bar,
 * metric tiles, section title with a count badge, segmented control, quiet activity strip and a compact empty state.
 * Pure presentation — every colour comes from the management theme (light + dark) and the brand orange; no glass.
 *
 * @module components/management/FollowUpAgendaKit
 */

import React from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Icon from './SafeIcon';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';

/**
 * Compact header: gradient icon tile, greeting + date, optional slim progress bar ("X / Y kész ma").
 * @param {{greeting: string, subtitle: string, icon?: string, progress?: {ratio: number, label: string}|null}} props props;
 *   pass `progress` only when today has to-dos.
 * @returns {JSX.Element} header.
 */
export const AgendaHeader = ({greeting, subtitle, icon = 'clipboard-check-outline', progress}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.header, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <View style={styles.headerTop}>
                <LinearGradient colors={BRAND.gradient} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.headerIcon}>
                    <Icon name={icon} size={24} color="#FFFFFF" />
                </LinearGradient>
                <View style={{flex: 1}}>
                    <Text style={[styles.headerTitle, {color: m.colors.heading}]} numberOfLines={1}>{greeting}</Text>
                    <Text style={[styles.headerSub, {color: m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>
                </View>
            </View>
            {!!progress && (
                <View style={styles.progressWrap}>
                    <View style={[styles.progressTrack, {backgroundColor: m.colors.chipBg}]}>
                        <View style={[styles.progressFill, {width: `${Math.round(Math.max(0, Math.min(1, progress.ratio)) * 100)}%`}]} />
                    </View>
                    <Text style={[styles.progressText, {color: m.colors.subText}]}>{progress.label}</Text>
                </View>
            )}
        </View>
    );
};

/**
 * Metric tile: icon, big number, label. Muted at 0; `accent` tints it when the value is above 0.
 * @param {{icon: string, label: string, value: number, accent?: string, active?: boolean, onPress?: () => void}} props props.
 * @returns {JSX.Element} tile.
 */
export const MetricTile = ({icon, label, value, accent = BRAND.base, active, onPress}) => {
    const m = useManagementTheme();
    const lit = value > 0 && accent !== 'neutral';
    const color = lit ? accent : m.colors.muted;

    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityState={{selected: !!active}}
            style={({pressed}) => [
                styles.tile,
                {backgroundColor: m.colors.surface, borderColor: active ? BRAND.base : m.colors.border, borderWidth: active ? 1.6 : 1},
                m.shadow(1),
                pressed && styles.pressed,
            ]}
        >
            <View style={[styles.tileIcon, {backgroundColor: withAlpha(color, m.isDark ? 0.2 : 0.12)}]}>
                <Icon name={icon} size={18} color={color} />
            </View>
            <Text style={[styles.tileValue, {color: lit ? m.colors.heading : m.colors.muted}]}>{value}</Text>
            <Text style={[styles.tileLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>
        </Pressable>
    );
};

/**
 * Section title: orange icon tile, title and a count badge.
 * @param {{icon: string, title: string, count?: number}} props props.
 * @returns {JSX.Element} title row.
 */
export const SectionTitle = ({icon, title, count}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.sectionRow}>
            <View style={[styles.sectionIcon, {backgroundColor: m.colors.chipBg}]}>
                <Icon name={icon} size={15} color={m.colors.primary} />
            </View>
            <Text style={[styles.sectionTitle, {color: m.colors.heading}]} numberOfLines={1}>{title}</Text>
            {count != null && count > 0 && (
                <View style={[styles.countBadge, {backgroundColor: m.colors.chipBg}]}>
                    <Text style={[styles.countText, {color: m.colors.chipText}]}>{count > 99 ? '99+' : count}</Text>
                </View>
            )}
        </View>
    );
};

/**
 * Segmented control with an orange selected state.
 * @param {{options: Array<{value: *, label: string}>, value: *, onChange: (v: *) => void, label?: string}} props props.
 * @returns {JSX.Element} control.
 */
export const Segmented = ({options, value, onChange, label}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.segmentWrap}>
            {!!label && <Text style={[styles.segmentLabel, {color: m.colors.subText}]} numberOfLines={1}>{label}</Text>}
            <View style={[styles.segment, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                {options.map((o) => {
                    const active = o.value === value;

                    return (
                        <Pressable
                            key={String(o.value)}
                            onPress={() => onChange(o.value)}
                            accessibilityRole="tab"
                            accessibilityState={{selected: active}}
                            style={({pressed}) => [styles.segmentItem, active && {backgroundColor: BRAND.base}, pressed && !active && styles.pressed]}
                        >
                            <Text style={[styles.segmentText, {color: active ? '#FFFFFF' : m.colors.subText}]} numberOfLines={1}>{o.label}</Text>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
};

/**
 * One quiet row of "icon + text" facts; renders nothing when `items` is empty.
 * @param {{items: Array<{icon: string, text: string}>}} props props.
 * @returns {JSX.Element|null} strip.
 */
export const ActivityStrip = ({items}) => {
    const m = useManagementTheme();

    if (!items.length) {
        return null;
    }

    return (
        <View style={styles.strip}>
            {items.map((i) => (
                <View key={i.icon + i.text} style={styles.stripItem}>
                    <Icon name={i.icon} size={14} color={m.colors.muted} />
                    <Text style={[styles.stripText, {color: m.colors.subText}]} numberOfLines={1}>{i.text}</Text>
                </View>
            ))}
        </View>
    );
};

/**
 * Compact empty state: icon tile, title, helper text.
 * @param {{icon: string, title: string, text?: string, tone?: 'primary'|'success'|'info'}} props props.
 * @returns {JSX.Element} empty state.
 */
export const QuietEmpty = ({icon, title, text, tone = 'primary'}) => {
    const m = useManagementTheme();
    const color = m.toneColor(tone);

    return (
        <View style={[styles.empty, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            <View style={[styles.emptyIcon, {backgroundColor: m.toneTint(tone)}]}>
                <Icon name={icon} size={26} color={color} />
            </View>
            <Text style={[styles.emptyTitle, {color: m.colors.heading}]}>{title}</Text>
            {!!text && <Text style={[styles.emptyText, {color: m.colors.subText}]}>{text}</Text>}
        </View>
    );
};

const styles = StyleSheet.create({
    header: {borderRadius: 24, borderWidth: 1, padding: 16, marginBottom: 12},
    headerTop: {flexDirection: 'row', alignItems: 'center', gap: 14},
    headerIcon: {width: 48, height: 48, borderRadius: 16, alignItems: 'center', justifyContent: 'center'},
    headerTitle: {fontSize: 20, fontWeight: '900', letterSpacing: -0.3},
    headerSub: {fontSize: 13, fontWeight: '600', marginTop: 2},
    progressWrap: {marginTop: 14},
    progressTrack: {height: 6, borderRadius: 999, overflow: 'hidden'},
    progressFill: {height: 6, borderRadius: 999, backgroundColor: BRAND.base},
    progressText: {fontSize: 12, fontWeight: '800', marginTop: 6},
    pressed: {opacity: 0.72, transform: [{scale: 0.98}]},
    tile: {flex: 1, borderRadius: 20, paddingVertical: 12, paddingHorizontal: 10, alignItems: 'center', gap: 4},
    tileIcon: {width: 34, height: 34, borderRadius: 12, alignItems: 'center', justifyContent: 'center'},
    tileValue: {fontSize: 26, fontWeight: '900', letterSpacing: -0.5},
    tileLabel: {fontSize: 12, fontWeight: '800'},
    sectionRow: {flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 18, marginBottom: 10},
    sectionIcon: {width: 28, height: 28, borderRadius: 10, alignItems: 'center', justifyContent: 'center'},
    sectionTitle: {flexShrink: 1, fontSize: 16, fontWeight: '900', letterSpacing: -0.2},
    countBadge: {minWidth: 22, height: 22, borderRadius: 11, paddingHorizontal: 7, alignItems: 'center', justifyContent: 'center'},
    countText: {fontSize: 12, fontWeight: '900'},
    segmentWrap: {flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6},
    segmentLabel: {fontSize: 12.5, fontWeight: '800'},
    segment: {flexDirection: 'row', borderWidth: 1, borderRadius: 14, padding: 3, flex: 1},
    segmentItem: {flex: 1, borderRadius: 11, paddingVertical: 8, alignItems: 'center'},
    segmentText: {fontSize: 13, fontWeight: '800'},
    strip: {flexDirection: 'row', flexWrap: 'wrap', gap: 14, paddingHorizontal: 4, marginTop: 4, marginBottom: 2},
    stripItem: {flexDirection: 'row', alignItems: 'center', gap: 5},
    stripText: {fontSize: 12.5, fontWeight: '700'},
    empty: {borderRadius: 22, borderWidth: 1, paddingVertical: 24, paddingHorizontal: 20, alignItems: 'center', gap: 6},
    emptyIcon: {width: 52, height: 52, borderRadius: 18, alignItems: 'center', justifyContent: 'center', marginBottom: 6},
    emptyTitle: {fontSize: 16, fontWeight: '900', textAlign: 'center'},
    emptyText: {fontSize: 13, fontWeight: '600', textAlign: 'center', lineHeight: 18},
});
